/**
 * User Preferences Service
 *
 * Manages user preferences in Appwrite with intelligent defaults and synchronization.
 *
 * Preferences Schema:
 * - language: string (browser language, e.g., "en-US")
 * - timezone: string (e.g., "America/New_York")
 * - theme: "dark" | "light"
 * - country: string (ISO code, e.g., "US")
 * - client_id: string (Brainiacs Corporate ID)
 * - onboarding_completed: boolean
 *
 * Note: Credits are NOT stored in preferences - they are fetched
 * on each login and stored directly in the database.
 */

import { backendProvider } from './backend';
import { DEFAULT_LOCALE, normalizeLocale, isSupportedLocale } from '../constants/locales';
import {
  normalizeBackgroundEffect,
  parseAuroraColors,
  serializeAuroraColors,
  type AuroraColorStops,
  type BackgroundEffect,
  type StoredBackgroundEffect,
} from '../constants/backgroundEffects';

export interface UserPreferences {
  language?: string;
  timezone?: string;
  theme?: 'dark' | 'light';
  country?: string;
  client_id?: string;
  onboarding_completed?: boolean;

  // Removed effects remain in the stored union so legacy values type-check and
  // normalize to Minimalist when read.
  background_effect?: StoredBackgroundEffect;
  background_opacity?: number;
  /** Comma-separated 3 hex stops for the Aurora ramp (see parseAuroraColors). */
  background_aurora_colors?: string;

  // Nexus personal AI server instance URL (e.g. "https://abcdef.brainiacs.nexus"),
  // written by the nexus_setup workflow once the server is provisioned.
  nexus_url?: string;

  // Metadata
  city?: string;
  region?: string;
  preferences_initialized_at?: string;
  preferences_version?: string;
}

export class PreferencesService {
  private cache: UserPreferences | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 60000; // 1 minute

  /**
   * Get current user preferences
   * Cached for 1 minute to reduce API calls
   */
  async getPreferences(): Promise<UserPreferences> {
    try {
      // Return cached if fresh
      const now = Date.now();
      if (this.cache && (now - this.cacheTimestamp) < this.CACHE_TTL) {
        return this.cache;
      }

      // Fetch from Appwrite
      const prefs = await backendProvider.auth.getPreferences();

      // Update cache
      this.cache = prefs as UserPreferences;
      this.cacheTimestamp = now;

      return this.cache;

    } catch (error: any) {
      console.error('❌ Failed to get preferences:', error);
      return {};
    }
  }

  /**
   * Update user preferences.
   *
   * The auth service merges server-side (fresh read + template defaults +
   * updates) — we pass ONLY the changed keys, never a cached snapshot, so a
   * stale cache can never replay an old prefs object over a newer one (the
   * disappearing-profilePictureUrl bug).
   */
  async updatePreferences(updates: Partial<UserPreferences>): Promise<boolean> {
    try {
      console.log('📝 Updating preferences:', updates);

      // Send the partial updates; merge happens in the auth service.
      await backendProvider.auth.updatePreferences(updates as Record<string, any>);

      // Refresh cache from the source of truth (server already has the merge).
      this.cache = null;

      console.log('✅ Preferences updated successfully');
      return true;

    } catch (error: any) {
      console.error('❌ Failed to update preferences:', error);
      return false;
    }
  }

  /**
   * Initialize client-side preferences on first login
   * Detects browser language and fills in missing defaults
   */
  async initializeClientPreferences(): Promise<void> {
    try {
      console.log('🔧 Initializing client-side preferences...');

      const current = await this.getPreferences();
      const updates: Partial<UserPreferences> = {};

      // 1. Detect and set language if missing. `language` is the SINGLE source
      //    of truth — migrate any legacy `preferred_language` value into it
      //    (registration used to write that key) so user prefs never carry
      //    two keys for the same thing.
      const currentPrefs = current as Record<string, any>;
      if (!current.language) {
        const legacy = typeof currentPrefs.preferred_language === 'string' ? currentPrefs.preferred_language : null;
        const raw = legacy || this.detectBrowserLanguage();
        updates.language = isSupportedLocale(raw) ? raw : normalizeLocale(raw);
        console.log(`🌐 Resolved user language: ${updates.language}${legacy ? ' (migrated from preferred_language)' : ''}`);
      }

      // 2. Set theme if missing (default: dark)
      if (!current.theme) {
        updates.theme = 'dark';
        console.log('🎨 Setting default theme: dark');
      }

      // 3. Set onboarding flag if missing
      if (current.onboarding_completed === undefined) {
        updates.onboarding_completed = false;
        console.log('📋 Setting onboarding flag: false');
      }

      // Update if there are any changes
      if (Object.keys(updates).length > 0) {
        await this.updatePreferences(updates);
        console.log('✅ Client preferences initialized:', updates);
      } else {
        console.log('✅ Client preferences already initialized');
      }

    } catch (error: any) {
      console.error('❌ Failed to initialize client preferences:', error);
    }
  }

  /**
   * Detect browser language
   * Returns ISO language code (e.g., "en-US", "es-ES")
   */
  private detectBrowserLanguage(): string {
    if (typeof navigator === 'undefined') {
      return DEFAULT_LOCALE; // Default for server-side
    }

    // Try multiple sources. Real navigator values are kept as-is (tolerant field);
    // only the fallback is our canonical default.
    const language = navigator.language ||
                     (navigator as any).userLanguage ||
                     (navigator.languages && navigator.languages[0]) ||
                     DEFAULT_LOCALE;

    return language;
  }

  /**
   * Get user's theme preference
   * Returns "dark" or "light"
   */
  async getTheme(): Promise<'dark' | 'light'> {
    const prefs = await this.getPreferences();
    return prefs.theme || 'dark';
  }

  /**
   * Set user's theme preference
   */
  async setTheme(theme: 'dark' | 'light'): Promise<boolean> {
    return this.updatePreferences({ theme });
  }

  /**
   * Get user's language preference
   */
  async getLanguage(): Promise<string> {
    const prefs = await this.getPreferences();
    return prefs.language || DEFAULT_LOCALE;
  }

  /**
   * Set user's language preference.
   * This is a user-chosen preference, so we enforce a valid, supported BCP 47 tag.
   */
  async setLanguage(language: string): Promise<boolean> {
    const locale = isSupportedLocale(language) ? language : normalizeLocale(language);
    return this.updatePreferences({ language: locale });
  }

  /**
   * Get user's timezone
   */
  async getTimezone(): Promise<string> {
    const prefs = await this.getPreferences();
    return prefs.timezone || 'UTC';
  }

  /**
   * Get user's country
   */
  async getCountry(): Promise<string> {
    const prefs = await this.getPreferences();
    return prefs.country || 'US';
  }

  /**
   * Get user's client ID (Brainiacs Corporate)
   */
  async getClientId(): Promise<string> {
    const prefs = await this.getPreferences();
    return prefs.client_id || '';
  }

  /**
   * Get user's background effect preference
   */
  async getBackgroundEffect(): Promise<BackgroundEffect> {
    const prefs = await this.getPreferences();
    return normalizeBackgroundEffect(prefs.background_effect);
  }

  /**
   * get user's background opacity preference (0-1)
   */
  async getBackgroundOpacity(): Promise<number> {
    const prefs = await this.getPreferences();
    // Default to 0.7 if undefined
    return prefs.background_opacity !== undefined ? prefs.background_opacity : 0.7;
  }

  /**
   * Set user's background effect preference
   */
  async setBackgroundEffect(effect: BackgroundEffect): Promise<boolean> {
    return this.updatePreferences({ background_effect: effect });
  }

  /**
   * Set user's background opacity
   */
  async setBackgroundOpacity(opacity: number): Promise<boolean> {
    // Clamp between 0 and 1
    const safeOpacity = Math.max(0, Math.min(1, opacity));
    return this.updatePreferences({ background_opacity: safeOpacity });
  }

  /** Get the user's Aurora colour stops, falling back per-position to defaults. */
  async getAuroraColors(): Promise<AuroraColorStops> {
    const prefs = await this.getPreferences();
    return parseAuroraColors(prefs.background_aurora_colors);
  }

  /** Persist the Aurora colour stops as a normalized comma-separated string. */
  async setAuroraColors(colors: AuroraColorStops): Promise<boolean> {
    return this.updatePreferences({ background_aurora_colors: serializeAuroraColors(colors) });
  }

  /**
   * Check if user completed onboarding
   */
  async isOnboardingCompleted(): Promise<boolean> {
    const prefs = await this.getPreferences();
    return prefs.onboarding_completed || false;
  }

  /**
   * Mark onboarding as completed
   */
  async completeOnboarding(): Promise<boolean> {
    return this.updatePreferences({ onboarding_completed: true });
  }

  /**
   * Clear preferences cache
   * Call this on logout
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }
}

/**
 * Singleton instance
 */
export const preferencesService = new PreferencesService();
